import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '../../store/hooks';
import { UserRole } from '../../types';
import { showToast, DateUtils } from '../../utils';
import { useGetActiveManufacturersQuery } from '../manufacturer/manufacturers-api-slice';
import {
  useGetPaintSettingsQuery,
  useUpdatePaintSettingsMutation,
} from '../settings/system-settings-api-slice';
import { selectCurrentUser } from '../user/user-slice';
import NewVersionConfirmationDialog from './components/new-version-confirmation-dialog';
import PackagingCostCalculatorDialog from './components/packaging-cost-calculator-dialog';
import UtilityCostCalculatorDialog from './components/utility-cost-calculator-dialog';
import {
  useCreatePaintDataVersionMutation,
  useGetLatestVersionInfoQuery,
  useGetPaintDataVersionByIdQuery,
  useGetPaintDataVersionsByManufacturerIdQuery,
} from './paint-data-api-slice';
import { PackagingCostDto, PaintDataInputsDto, UtilityCostDto } from './paint-data-service';

const PaintDataManagementPage: React.FC = () => {
  const theme = useTheme();
  const currentUser = useAppSelector(selectCurrentUser);
  const isAdmin = currentUser?.role === UserRole.ADMINISTRATOR;
  const canCreateVersion = isAdmin || currentUser?.role === UserRole.SUPERVISOR;

  // Manufacturer selection
  const { data: manufacturers = [] } = useGetActiveManufacturersQuery(undefined, {
    skip: !isAdmin,
  });
  const [selectedManufacturerId, setSelectedManufacturerId] = useState<number | undefined>(
    isAdmin ? undefined : currentUser?.manufacturerId
  );

  // Set default manufacturer for admin
  useEffect(() => {
    if (isAdmin && manufacturers.length > 0 && !selectedManufacturerId) {
      setSelectedManufacturerId(manufacturers[0].id);
    }
  }, [isAdmin, manufacturers, selectedManufacturerId]);

  // Fetch versions
  const {
    data: versions = [],
    isLoading: versionsLoading,
    refetch: refetchVersions,
  } = useGetPaintDataVersionsByManufacturerIdQuery(selectedManufacturerId!, {
    skip: !selectedManufacturerId,
    refetchOnMountOrArgChange: true,
  });

  // Fetch latest version info
  const {
    data: latestVersionInfo,
    isLoading: isLoadingVersionInfo,
    error: versionInfoError,
    refetch: refetchLatestVersionInfo,
  } = useGetLatestVersionInfoQuery(selectedManufacturerId!, {
    skip: !selectedManufacturerId,
    refetchOnMountOrArgChange: true,
  });

  // Fetch system settings for defaults - use selectedManufacturerId for admin, undefined for others (uses token)
  const {
    data: paintSettings,
    isLoading: isLoadingPaintSettings,
    refetch: refetchPaintSettings,
  } = useGetPaintSettingsQuery(
    isAdmin && selectedManufacturerId ? selectedManufacturerId : undefined,
    {
      skip: !selectedManufacturerId,
      refetchOnMountOrArgChange: true,
    }
  );

  // State
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
  const [isCreatingNewVersion, setIsCreatingNewVersion] = useState(false);
  const [showUtilityCalculator, setShowUtilityCalculator] = useState(false);
  const [showPackagingCalculator, setShowPackagingCalculator] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  /** Per-hour totals come from calculator Accept only; initial 0 is not "entered" until user confirms. */
  const [utilityCostPerHourCommitted, setUtilityCostPerHourCommitted] = useState(false);
  const [packagingCostPerHourCommitted, setPackagingCostPerHourCommitted] = useState(false);

  // Form state for new version
  const [formData, setFormData] = useState<PaintDataInputsDto>({
    paintPricePerGallon: 0,
    surfaceAreaPerGallon: 0,
    surfaceAreaPerLinearFoot: 0,
    painterLaborRate: 0,
    numberOfPainters: 1,
    utilitiesCost: 0,
    packagingBandingCost: 0,
    avgLinearFtPainted: 0,
    workHoursPerMonth: 0,
    markupPercentPaint: 0,
  });

  const [utilityCosts, setUtilityCosts] = useState<UtilityCostDto[]>([]);
  const [packagingCosts, setPackagingCosts] = useState<PackagingCostDto[]>([]);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  // Track raw input strings for formatted fields to allow free typing
  const [rawInputValues, setRawInputValues] = useState<{
    [key: string]: string;
  }>({});

  // Fetch selected version (RTK Query may retain previous `data` while skipped — see selectedVersion below)
  const { data: selectedVersionQueryData } = useGetPaintDataVersionByIdQuery(selectedVersionId!, {
    skip: !selectedVersionId || isCreatingNewVersion,
  });

  const selectedVersion = useMemo(() => {
    if (selectedVersionId == null || !selectedVersionQueryData || selectedManufacturerId == null) {
      return undefined;
    }
    if (selectedVersionQueryData.id !== selectedVersionId) {
      return undefined;
    }
    if (selectedVersionQueryData.manufacturerId !== selectedManufacturerId) {
      return undefined;
    }
    return selectedVersionQueryData;
  }, [selectedVersionId, selectedManufacturerId, selectedVersionQueryData]);

  // Create version mutation
  const [createVersion, { isLoading: isCreating }] = useCreatePaintDataVersionMutation();
  const [updatePaintSettings] = useUpdatePaintSettingsMutation();

  // Calculate derived values
  const calculatedValues = useMemo(() => {
    if (isCreatingNewVersion) {
      const avgLinearFootPerHour =
        formData.workHoursPerMonth > 0
          ? formData.avgLinearFtPainted / formData.workHoursPerMonth
          : 0;
      const totalSquareInchesPerHour = avgLinearFootPerHour * formData.surfaceAreaPerLinearFoot;
      const totalGallonsPerHour =
        formData.surfaceAreaPerGallon > 0
          ? totalSquareInchesPerHour / formData.surfaceAreaPerGallon
          : 0;
      const paintCostPerHour = formData.paintPricePerGallon * totalGallonsPerHour;
      const laborCostPerHour = formData.painterLaborRate * formData.numberOfPainters;
      const subtotalPerHour =
        paintCostPerHour +
        laborCostPerHour +
        formData.utilitiesCost +
        formData.packagingBandingCost;
      // Total cost equals subtotal - markup is no longer applied in paint calculations
      const totalCostPerHour = subtotalPerHour;
      const totalCostPerSquareInch =
        totalSquareInchesPerHour > 0 ? subtotalPerHour / totalSquareInchesPerHour : 0;
      const paintCostPerLinearFoot =
        avgLinearFootPerHour > 0 ? subtotalPerHour / avgLinearFootPerHour : 0;
      const totalCostPerLinearFoot = avgLinearFootPerHour > 0 ? paintCostPerLinearFoot : 0;

      return {
        averageLinearFootPerHour: avgLinearFootPerHour,
        totalSquareInchesPerHour,
        totalGallonsPerHour,
        paintCostPerHour,
        laborCostPerHour,
        utilitiesCostPerHour: formData.utilitiesCost,
        packagingCostPerHour: formData.packagingBandingCost,
        subtotalPerHour,
        totalCostPerHour,
        totalCostPerLinearFoot,
        totalCostPerSquareInch,
        paintCostPerLinearFoot,
      };
    }
    return selectedVersion?.calculationResults;
  }, [isCreatingNewVersion, formData, selectedVersion]);

  // Initialize form with defaults when creating new version
  useEffect(() => {
    if (isCreatingNewVersion && paintSettings) {
      // Clear all fields and use system settings defaults where applicable
      setFormData({
        paintPricePerGallon: 0,
        surfaceAreaPerGallon: paintSettings.surfaceAreaPerCoveredPerGallon || 0,
        surfaceAreaPerLinearFoot: paintSettings.surfaceAreaPerLinearFoot || 0,
        painterLaborRate: 0,
        numberOfPainters: 1,
        utilitiesCost: 0,
        packagingBandingCost: 0,
        avgLinearFtPainted: 0,
        workHoursPerMonth: paintSettings.workHoursPerMonth || 0,
        markupPercentPaint: 0, // Markup is no longer used - always set to 0
      });

      // Initialize utility costs from system settings
      setUtilityCosts(
        paintSettings.utilityCostList
          .filter(type => type && type.trim().length > 0)
          .map(type => ({ utilityType: type.trim(), monthlyCost: 0 }))
      );

      // Initialize packaging costs from system settings
      setPackagingCosts(
        paintSettings.packagingCostList
          .filter(type => type && type.trim().length > 0)
          .map(type => ({ packagingType: type.trim(), monthlyCost: 0 }))
      );

      setFormErrors({});
      setSelectedVersionId(null);
      setRawInputValues({}); // Clear raw inputs when initializing
      setUtilityCostPerHourCommitted(false);
      setPackagingCostPerHourCommitted(false);
    }
  }, [isCreatingNewVersion, paintSettings]);

  // Handle manufacturer change
  const handleManufacturerChange = (event: SelectChangeEvent<number>) => {
    const manufacturerId = event.target.value as number;
    setSelectedManufacturerId(manufacturerId);
    setSelectedVersionId(null);
    setIsCreatingNewVersion(false);
    setFormData({
      paintPricePerGallon: 0,
      surfaceAreaPerGallon: 0,
      surfaceAreaPerLinearFoot: 0,
      painterLaborRate: 0,
      numberOfPainters: 1,
      utilitiesCost: 0,
      packagingBandingCost: 0,
      avgLinearFtPainted: 0,
      workHoursPerMonth: 0,
      markupPercentPaint: 0,
    });
    setUtilityCosts([]);
    setPackagingCosts([]);
    setFormErrors({});
    setRawInputValues({});
    setShowUtilityCalculator(false);
    setShowPackagingCalculator(false);
    setShowConfirmationDialog(false);
    setUtilityCostPerHourCommitted(false);
    setPackagingCostPerHourCommitted(false);
  };

  // Refetch data when manufacturer changes to ensure fresh data
  // This ensures that if another user created a version while viewing a different manufacturer,
  // switching back will show the latest data
  useEffect(() => {
    if (selectedManufacturerId) {
      refetchVersions();
      refetchLatestVersionInfo();
      refetchPaintSettings();
    }
  }, [selectedManufacturerId, refetchVersions, refetchLatestVersionInfo, refetchPaintSettings]);

  // Handle version selection
  const handleVersionSelect = (versionId: number) => {
    setSelectedVersionId(versionId);
    setIsCreatingNewVersion(false);
    setFormErrors({});
    setRawInputValues({});
    setUtilityCostPerHourCommitted(false);
    setPackagingCostPerHourCommitted(false);
  };

  // Handle create new version
  const handleCreateNewVersion = () => {
    setIsCreatingNewVersion(true);
    setSelectedVersionId(null);
  };

  // Handle form field change
  const handleFieldChange = (field: keyof PaintDataInputsDto, value: number | string) => {
    // Enforce max 4 decimal places for surfaceAreaPerLinearFoot
    if (field === 'surfaceAreaPerLinearFoot' && typeof value === 'string' && value !== '') {
      const dotIndex = value.indexOf('.');
      if (dotIndex !== -1 && value.length - dotIndex - 1 > 4) {
        return;
      }
    }

    // Store raw string value for formatted fields to allow free typing
    if (typeof value === 'string') {
      setRawInputValues(prev => ({ ...prev, [field]: value }));
      // Only update formData if the value is a valid number or empty
      if (value === '' || value === '-') {
        setFormData(prev => ({ ...prev, [field]: 0 }));
      } else {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          setFormData(prev => ({ ...prev, [field]: numValue }));
        }
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
      // Clear raw input when setting numeric value directly
      setRawInputValues(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
    // Clear error for this field
    const newErrors = { ...formErrors };
    delete newErrors[field];
    setFormErrors(newErrors);
  };

  // Handle blur - format the value on blur
  const handleFieldBlur = (field: keyof PaintDataInputsDto) => {
    // Clear raw input so formatted value displays
    setRawInputValues(prev => {
      const updated = { ...prev };
      delete updated[field];
      return updated;
    });
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (formData.paintPricePerGallon <= 0) {
      errors.paintPricePerGallon = 'Please enter a valid Paint Price greater than zero.';
    }
    if (formData.surfaceAreaPerGallon <= 0) {
      errors.surfaceAreaPerGallon = 'Please enter a valid Surface Area greater than zero.';
    }
    if (formData.surfaceAreaPerLinearFoot <= 0) {
      errors.surfaceAreaPerLinearFoot =
        'Please enter a valid Surface Area per Linear Foot greater than zero.';
    } else {
      const decimalPart = formData.surfaceAreaPerLinearFoot.toString().split('.')[1];
      if (decimalPart && decimalPart.length > 4) {
        errors.surfaceAreaPerLinearFoot =
          'Surface Area per Linear Foot must have no more than 4 decimal places.';
      }
    }
    if (formData.painterLaborRate <= 0) {
      errors.painterLaborRate = 'Please enter a valid Labor Rate greater than zero.';
    }
    if (formData.numberOfPainters <= 0) {
      errors.numberOfPainters =
        'Please enter a valid number for the Number of Painters that is greater than zero.';
    }
    if (!utilityCostPerHourCommitted || formData.utilitiesCost < 0) {
      errors.utilitiesCost = 'Please enter a valid Utilities cost greater than or equal to zero.';
    }
    if (!packagingCostPerHourCommitted || formData.packagingBandingCost < 0) {
      errors.packagingBandingCost =
        'Please enter a valid Packaging cost greater than or equal to zero.';
    }
    if (formData.avgLinearFtPainted <= 0) {
      errors.avgLinearFtPainted = 'Please enter a valid Average Linear Feet greater than zero.';
    }
    if (formData.workHoursPerMonth <= 0) {
      errors.workHoursPerMonth = 'Please enter valid Work Hours greater than zero.';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle save
  const handleSave = () => {
    if (!validateForm()) {
      return;
    }
    // If version info is still loading, wait
    if (isLoadingVersionInfo) {
      return;
    }
    setShowConfirmationDialog(true);
  };

  // Handle confirm create
  const handleConfirmCreate = async (notes: string) => {
    if (!selectedManufacturerId) {
      showToast('Please select a manufacturer', 'error');
      return;
    }

    // Filter out any utility/packaging costs with empty type strings
    // and ensure we have valid data - trim all type strings
    const validUtilityCosts = utilityCosts
      .filter(cost => cost.utilityType && cost.utilityType.trim().length > 0)
      .map(cost => ({
        utilityType: cost.utilityType.trim(),
        monthlyCost: cost.monthlyCost,
      }));
    const validPackagingCosts = packagingCosts
      .filter(cost => cost.packagingType && cost.packagingType.trim().length > 0)
      .map(cost => ({
        packagingType: cost.packagingType.trim(),
        monthlyCost: cost.monthlyCost,
      }));

    // If no utility/packaging costs were set, initialize from system settings
    // Filter out empty strings and ensure all types are trimmed
    const finalUtilityCosts =
      validUtilityCosts.length > 0
        ? validUtilityCosts
        : (paintSettings?.utilityCostList || [])
            .filter(type => type && type.trim().length > 0)
            .map(type => ({ utilityType: type.trim(), monthlyCost: 0 }));
    const finalPackagingCosts =
      validPackagingCosts.length > 0
        ? validPackagingCosts
        : (paintSettings?.packagingCostList || [])
            .filter(type => type && type.trim().length > 0)
            .map(type => ({ packagingType: type.trim(), monthlyCost: 0 }));

    try {
      await createVersion({
        manufacturerId: selectedManufacturerId,
        notes: notes || '', // API requires string, send empty if not provided
        paintInputs: formData,
        utilityCosts: finalUtilityCosts,
        packagingCosts: finalPackagingCosts,
      }).unwrap();

      showToast('Paint data version created successfully', 'success');

      // Sync any newly added cost types back to System Settings
      const originalUtilityTypes = new Set(
        (paintSettings?.utilityCostList || []).map(t => t.trim().toLowerCase())
      );
      const originalPackagingTypes = new Set(
        (paintSettings?.packagingCostList || []).map(t => t.trim().toLowerCase())
      );

      const newUtilityTypes = finalUtilityCosts
        .map(c => c.utilityType.trim())
        .filter(t => t && !originalUtilityTypes.has(t.toLowerCase()));
      const newPackagingTypes = finalPackagingCosts
        .map(c => c.packagingType.trim())
        .filter(t => t && !originalPackagingTypes.has(t.toLowerCase()));

      if ((newUtilityTypes.length > 0 || newPackagingTypes.length > 0) && paintSettings) {
        try {
          await updatePaintSettings({
            manufacturerId: isAdmin && selectedManufacturerId ? selectedManufacturerId : undefined,
            paintSettings: {
              surfaceAreaPerCoveredPerGallon: paintSettings.surfaceAreaPerCoveredPerGallon,
              surfaceAreaPerLinearFoot: paintSettings.surfaceAreaPerLinearFoot,
              markupPercentage: paintSettings.markupPercentage,
              workHoursPerMonth: paintSettings.workHoursPerMonth,
              utilityCostList: [...(paintSettings.utilityCostList || []), ...newUtilityTypes],
              packagingCostList: [...(paintSettings.packagingCostList || []), ...newPackagingTypes],
            },
          }).unwrap();
          void refetchPaintSettings();
        } catch {
          showToast(
            'Version saved, but failed to sync new cost types to System Settings.',
            'warning'
          );
        }
      }

      setIsCreatingNewVersion(false);
      setShowConfirmationDialog(false);
      refetchVersions();
      void refetchLatestVersionInfo();
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === 'object' && 'data' in error
          ? String((error as { data?: { message?: string } }).data?.message || 'Unknown error')
          : 'Failed to create paint data version';
      showToast(errorMessage, 'error');
    }
  };

  // Next version: use both the version list and latest-info endpoint so we stay correct if one refetch lags.
  const getNextVersionNumber = (): number => {
    const maxFromVersions = versions.reduce((m, v) => Math.max(m, v.versionNumber), 0);
    const maxFromInfo = latestVersionInfo?.version ?? 0;
    const currentMax = Math.max(maxFromVersions, maxFromInfo);

    if (isLoadingVersionInfo && currentMax === 0 && versions.length === 0) {
      return 1;
    }
    if ((versionInfoError || !latestVersionInfo) && currentMax === 0) {
      return 1;
    }
    return currentMax + 1;
  };

  // Handle cancel
  const handleCancel = () => {
    setIsCreatingNewVersion(false);
    setShowConfirmationDialog(false);
    setFormData({
      paintPricePerGallon: 0,
      surfaceAreaPerGallon: 0,
      surfaceAreaPerLinearFoot: 0,
      painterLaborRate: 0,
      numberOfPainters: 1,
      utilitiesCost: 0,
      packagingBandingCost: 0,
      avgLinearFtPainted: 0,
      workHoursPerMonth: 0,
      markupPercentPaint: 0,
    });
    setUtilityCosts([]);
    setPackagingCosts([]);
    setFormErrors({});
    setRawInputValues({}); // Clear raw inputs on cancel
    setUtilityCostPerHourCommitted(false);
    setPackagingCostPerHourCommitted(false);
    if (versions.length > 0) {
      setSelectedVersionId(versions[0].id);
    }
  };

  // Handle utility calculator
  const handleUtilityCalculatorAccept = (
    _monthlyCost: number,
    costPerHour: number,
    costs: UtilityCostDto[]
  ) => {
    setFormData(prev => ({ ...prev, utilitiesCost: costPerHour }));
    setUtilityCosts(costs);
    setUtilityCostPerHourCommitted(true);
    setShowUtilityCalculator(false);
    setFormErrors(prev => {
      const next = { ...prev };
      delete next.utilitiesCost;
      return next;
    });
  };

  // Handle packaging calculator
  const handlePackagingCalculatorAccept = (
    _monthlyCost: number,
    costPerHour: number,
    costs: PackagingCostDto[]
  ) => {
    setFormData(prev => ({ ...prev, packagingBandingCost: costPerHour }));
    setPackagingCosts(costs);
    setPackagingCostPerHourCommitted(true);
    setShowPackagingCalculator(false);
    setFormErrors(prev => {
      const next = { ...prev };
      delete next.packagingBandingCost;
      return next;
    });
  };

  // After utility/packaging calculators run, per-hour costs = monthly totals ÷ work hours.
  // Recalculate when work hours change so read-only fields stay correct (fields appear above Work Hours in the form).
  useEffect(() => {
    if (!isCreatingNewVersion) return;

    const wh = formData.workHoursPerMonth;

    setFormData(prev => {
      let utilitiesCost = prev.utilitiesCost;
      let packagingBandingCost = prev.packagingBandingCost;

      if (utilityCostPerHourCommitted && utilityCosts.length > 0) {
        const monthlyTotal = utilityCosts.reduce((s, c) => s + c.monthlyCost, 0);
        utilitiesCost = wh > 0 ? monthlyTotal / wh : 0;
      }

      if (packagingCostPerHourCommitted && packagingCosts.length > 0) {
        const monthlyTotal = packagingCosts.reduce((s, c) => s + c.monthlyCost, 0);
        packagingBandingCost = wh > 0 ? monthlyTotal / wh : 0;
      }

      if (
        utilitiesCost === prev.utilitiesCost &&
        packagingBandingCost === prev.packagingBandingCost
      ) {
        return prev;
      }

      return { ...prev, utilitiesCost, packagingBandingCost };
    });
  }, [
    isCreatingNewVersion,
    formData.workHoursPerMonth,
    utilityCosts,
    packagingCosts,
    utilityCostPerHourCommitted,
    packagingCostPerHourCommitted,
  ]);

  // Format currency
  const formatCurrency = (value: number): string => {
    return value.toFixed(2);
  };

  // Format number with precision
  const formatNumber = (value: number, precision: number = 2): string => {
    return value.toFixed(precision);
  };

  // Get display data (either from selected version or form)
  const displayData = isCreatingNewVersion
    ? {
        inputs: formData,
        calculationResults: calculatedValues,
        utilityCosts,
        packagingCosts,
        isReadOnly: false,
      }
    : selectedVersion
      ? {
          inputs: selectedVersion.inputs,
          calculationResults: selectedVersion.calculationResults,
          utilityCosts: selectedVersion.utilityCosts,
          packagingCosts: selectedVersion.packagingCosts,
          isReadOnly: true,
        }
      : null;

  if (!selectedManufacturerId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Please select a manufacturer to view paint data.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Paint Data Management
      </Typography>

      {/* Manufacturer Selection (Admin only) */}
      {isAdmin && (
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth sx={{ maxWidth: 400 }}>
            <InputLabel>Manufacturer</InputLabel>
            <Select
              value={selectedManufacturerId || ''}
              onChange={handleManufacturerChange}
              label="Manufacturer"
            >
              {manufacturers.map(manufacturer => (
                <MenuItem key={manufacturer.id} value={manufacturer.id}>
                  {manufacturer.licenseeName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {versionsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Version History Panel */}
          <Grid item xs={12} md={4}>
            <Paper
              sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                maxHeight: 'calc(100vh - 200px)',
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, flexShrink: 0 }}>
                Version History
              </Typography>

              <Box sx={{ overflow: 'auto', mb: 2, flexShrink: 1, minHeight: 0 }}>
                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '25%' }}>Version</TableCell>
                      <TableCell sx={{ width: '35%' }}>Created</TableCell>
                      <TableCell sx={{ width: '40%' }}>Created By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {versions.map(version => {
                      const isSelected = selectedVersionId === version.id;
                      return (
                        <TableRow
                          key={version.id}
                          onClick={() => handleVersionSelect(version.id)}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor: isSelected ? 'transparent' : 'transparent',
                            borderLeft: isSelected
                              ? `4px solid ${theme.palette.primary.main}`
                              : '4px solid transparent',
                            '&:hover': {
                              backgroundColor: theme.palette.action.hover,
                            },
                            '& td': {
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              height: '48px',
                              padding: '8px 16px',
                              fontWeight: isSelected ? 600 : 400,
                              color: isSelected ? theme.palette.primary.main : 'inherit',
                            },
                          }}
                        >
                          <TableCell>{version.versionNumber}</TableCell>
                          <TableCell>{DateUtils.toDisplayDate(version.created)}</TableCell>
                          <TableCell title={version.createdByUserName || 'Unknown'}>
                            {version.createdByUserName || 'Unknown'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>

              {canCreateVersion && (
                <Button
                  variant="contained"
                  onClick={handleCreateNewVersion}
                  disabled={isCreatingNewVersion}
                  sx={{ mt: 'auto', flexShrink: 0 }}
                >
                  Create New Version
                </Button>
              )}

              {/* Version Details */}
              {selectedVersion && !isCreatingNewVersion && (
                <Box
                  sx={{
                    mt: 2,
                    pt: 2,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    flexShrink: 0,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Version Details
                  </Typography>
                  <Typography variant="body2">
                    <strong>Version:</strong> {selectedVersion.versionNumber}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Created:</strong> {DateUtils.toDisplayDateTime(selectedVersion.created)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Created By:</strong> {selectedVersion.createdByUserName}
                  </Typography>
                  {selectedVersion.notes && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Notes:</strong> {selectedVersion.notes}
                    </Typography>
                  )}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Wet Paint Workup Panel */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Wet Paint Workup
              </Typography>

              {displayData ? (
                <Box>
                  <TextField
                    fullWidth
                    label="Paint Price (per gallon)"
                    type="number"
                    value={
                      displayData.isReadOnly
                        ? displayData.inputs.paintPricePerGallon
                          ? formatNumber(displayData.inputs.paintPricePerGallon, 2)
                          : ''
                        : rawInputValues.paintPricePerGallon !== undefined
                          ? rawInputValues.paintPricePerGallon
                          : displayData.inputs.paintPricePerGallon
                            ? formatNumber(displayData.inputs.paintPricePerGallon, 2)
                            : ''
                    }
                    onChange={e => handleFieldChange('paintPricePerGallon', e.target.value)}
                    onBlur={() => handleFieldBlur('paintPricePerGallon')}
                    disabled={displayData.isReadOnly}
                    required={!displayData.isReadOnly}
                    error={!!formErrors.paintPricePerGallon}
                    helperText={formErrors.paintPricePerGallon}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    inputProps={{
                      min: 0,
                      step: 0.01,
                    }}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Surface Area Covered per Gallon (sq in)"
                    type="number"
                    value={displayData.inputs.surfaceAreaPerGallon || ''}
                    onChange={e => handleFieldChange('surfaceAreaPerGallon', e.target.value)}
                    disabled={displayData.isReadOnly}
                    required={!displayData.isReadOnly}
                    error={!!formErrors.surfaceAreaPerGallon}
                    helperText={formErrors.surfaceAreaPerGallon}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">inches</InputAdornment>,
                    }}
                    inputProps={{
                      min: 0,
                      step: 0.01,
                    }}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Surface Area per Linear Foot"
                    type="number"
                    value={displayData.inputs.surfaceAreaPerLinearFoot || ''}
                    onChange={e => handleFieldChange('surfaceAreaPerLinearFoot', e.target.value)}
                    disabled={displayData.isReadOnly}
                    required={!displayData.isReadOnly}
                    error={!!formErrors.surfaceAreaPerLinearFoot}
                    helperText={formErrors.surfaceAreaPerLinearFoot}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">feet</InputAdornment>,
                    }}
                    inputProps={{
                      min: 0,
                      step: 0.0001,
                    }}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Painter Labor Rate (per hour, per painter)"
                    type="number"
                    value={
                      displayData.isReadOnly
                        ? displayData.inputs.painterLaborRate
                          ? formatNumber(displayData.inputs.painterLaborRate, 2)
                          : ''
                        : rawInputValues.painterLaborRate !== undefined
                          ? rawInputValues.painterLaborRate
                          : displayData.inputs.painterLaborRate
                            ? formatNumber(displayData.inputs.painterLaborRate, 2)
                            : ''
                    }
                    onChange={e => handleFieldChange('painterLaborRate', e.target.value)}
                    onBlur={() => handleFieldBlur('painterLaborRate')}
                    disabled={displayData.isReadOnly}
                    required={!displayData.isReadOnly}
                    error={!!formErrors.painterLaborRate}
                    helperText={formErrors.painterLaborRate}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      endAdornment: <InputAdornment position="end">per hour</InputAdornment>,
                    }}
                    inputProps={{
                      min: 0,
                      step: 0.01,
                    }}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Number of Painters"
                    type="number"
                    value={displayData.inputs.numberOfPainters || ''}
                    onChange={e => handleFieldChange('numberOfPainters', e.target.value)}
                    disabled={displayData.isReadOnly}
                    required={!displayData.isReadOnly}
                    error={!!formErrors.numberOfPainters}
                    helperText={formErrors.numberOfPainters}
                    inputProps={{
                      min: 1,
                      step: 1,
                    }}
                    sx={{ mb: 2 }}
                  />

                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <TextField
                      fullWidth
                      label="Utilities Cost (per hour)"
                      type="text"
                      value={
                        displayData.isReadOnly || utilityCostPerHourCommitted
                          ? formatNumber(displayData.inputs.utilitiesCost ?? 0, 2)
                          : ''
                      }
                      disabled
                      required={!displayData.isReadOnly}
                      error={!!formErrors.utilitiesCost}
                      helperText={formErrors.utilitiesCost}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        endAdornment: <InputAdornment position="end">per hour</InputAdornment>,
                      }}
                    />
                    {!displayData.isReadOnly && (
                      <Button
                        variant="outlined"
                        onClick={() => setShowUtilityCalculator(true)}
                        sx={{ minWidth: 40, mt: 1 }}
                      >
                        🧮
                      </Button>
                    )}
                  </Box>

                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <TextField
                      fullWidth
                      label="Packaging/Banding Cost (per hour)"
                      type="text"
                      value={
                        displayData.isReadOnly || packagingCostPerHourCommitted
                          ? formatNumber(displayData.inputs.packagingBandingCost ?? 0, 2)
                          : ''
                      }
                      disabled
                      required={!displayData.isReadOnly}
                      error={!!formErrors.packagingBandingCost}
                      helperText={formErrors.packagingBandingCost}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        endAdornment: <InputAdornment position="end">per hour</InputAdornment>,
                      }}
                    />
                    {!displayData.isReadOnly && (
                      <Button
                        variant="outlined"
                        onClick={() => setShowPackagingCalculator(true)}
                        sx={{ minWidth: 40, mt: 1 }}
                      >
                        🧮
                      </Button>
                    )}
                  </Box>

                  <TextField
                    fullWidth
                    label="Avg Linear ft of truss painted (per month)"
                    type="number"
                    value={displayData.inputs.avgLinearFtPainted || ''}
                    onChange={e => handleFieldChange('avgLinearFtPainted', e.target.value)}
                    disabled={displayData.isReadOnly}
                    required={!displayData.isReadOnly}
                    error={!!formErrors.avgLinearFtPainted}
                    helperText={formErrors.avgLinearFtPainted}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">feet</InputAdornment>,
                    }}
                    inputProps={{
                      min: 0,
                      step: 1,
                    }}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Work Hours (per month)"
                    type="number"
                    value={displayData.inputs.workHoursPerMonth || ''}
                    onChange={e => handleFieldChange('workHoursPerMonth', e.target.value)}
                    disabled={displayData.isReadOnly}
                    required={!displayData.isReadOnly}
                    error={!!formErrors.workHoursPerMonth}
                    helperText={
                      formErrors.workHoursPerMonth
                        ? formErrors.workHoursPerMonth
                        : !displayData.isReadOnly &&
                            (utilityCostPerHourCommitted || packagingCostPerHourCommitted)
                          ? 'Changing work hours updates utility and packaging costs per hour automatically.'
                          : undefined
                    }
                    InputProps={{
                      endAdornment: <InputAdornment position="end">hours</InputAdornment>,
                    }}
                    inputProps={{
                      min: 0,
                      step: 1,
                    }}
                    sx={{ mb: 2 }}
                  />

                  {!displayData.isReadOnly && (
                    <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                      <Button variant="outlined" onClick={handleCancel} disabled={isCreating}>
                        Cancel
                      </Button>
                      <Button variant="contained" onClick={handleSave} disabled={isCreating}>
                        {isCreating ? 'Saving...' : 'Save'}
                      </Button>
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Select a version or create a new one to view details.
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Standard Costs Panel */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Standard Costs
              </Typography>

              {displayData?.calculationResults ? (
                <Box>
                  <TextField
                    fullWidth
                    label="Average Linear Foot (of truss painted per hour)"
                    value={formatNumber(displayData.calculationResults.averageLinearFootPerHour, 2)}
                    disabled
                    InputProps={{
                      endAdornment: <InputAdornment position="end">feet</InputAdornment>,
                    }}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Total Square Inches of Truss Painted (per hour)"
                    value={formatNumber(displayData.calculationResults.totalSquareInchesPerHour, 2)}
                    disabled
                    InputProps={{
                      endAdornment: <InputAdornment position="end">inches</InputAdornment>,
                    }}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Total Gallons of Paint (used per hour)"
                    value={formatNumber(displayData.calculationResults.totalGallonsPerHour, 2)}
                    disabled
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Paint Cost (per hour)"
                    value={formatCurrency(displayData.calculationResults.paintCostPerHour)}
                    disabled
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      endAdornment: <InputAdornment position="end">per hour</InputAdornment>,
                    }}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Labor Cost (per hour)"
                    value={formatCurrency(displayData.calculationResults.laborCostPerHour)}
                    disabled
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      endAdornment: <InputAdornment position="end">per hour</InputAdornment>,
                    }}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Subtotal (per hour)"
                    value={formatCurrency(displayData.calculationResults.subtotalPerHour)}
                    disabled
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      endAdornment: <InputAdornment position="end">per hour</InputAdornment>,
                    }}
                    sx={{ mb: 2 }}
                  />

                  <Divider sx={{ my: 2 }} />

                  <TextField
                    fullWidth
                    label="Total Cost (per sq in)"
                    value={formatCurrency(displayData.calculationResults.totalCostPerSquareInch)}
                    disabled
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Paint Cost/Linear Foot"
                    value={formatCurrency(displayData.calculationResults.paintCostPerLinearFoot)}
                    disabled
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    sx={{ mb: 2 }}
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Select a version or create a new one to view calculated costs.
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Calculator Dialogs */}
      <UtilityCostCalculatorDialog
        open={showUtilityCalculator}
        onClose={() => setShowUtilityCalculator(false)}
        onAccept={handleUtilityCalculatorAccept}
        initialUtilityCosts={utilityCosts}
        workHoursPerMonth={formData.workHoursPerMonth}
        isNewVersion={isCreatingNewVersion}
        systemSettingsUtilityCosts={paintSettings?.utilityCostList || []}
        isLoadingSettings={isLoadingPaintSettings}
      />

      <PackagingCostCalculatorDialog
        open={showPackagingCalculator}
        onClose={() => setShowPackagingCalculator(false)}
        onAccept={handlePackagingCalculatorAccept}
        initialPackagingCosts={packagingCosts}
        workHoursPerMonth={formData.workHoursPerMonth}
        isNewVersion={isCreatingNewVersion}
        systemSettingsPackagingCosts={paintSettings?.packagingCostList || []}
        isLoadingSettings={isLoadingPaintSettings}
      />

      {/* Confirmation Dialog */}
      <NewVersionConfirmationDialog
        open={showConfirmationDialog}
        onClose={() => setShowConfirmationDialog(false)}
        onConfirm={handleConfirmCreate}
        versionNumber={getNextVersionNumber()}
      />
    </Box>
  );
};

export default PaintDataManagementPage;
